import { useLocation } from "react-router-dom";
import { useEffect, useMemo } from "react";
import NewHeader from "@/components/NewHeader";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { useLanguage } from "@/contexts/LanguageContext";
import { getLocalizedUrl, getPathWithoutLanguage } from "@/utils/languageUtils";
import { getPageSEO } from "@/data/seoTranslations";
import type { SEOLanguage } from "@/data/seoTranslations";
import { OptimizedImage } from "@/components/OptimizedImage";
import { getNotFoundBlurPlaceholder } from "@/utils/notFoundBlurPlaceholders";

/** WebP backgrounds (JPEG masters remain in repo for re-encoding). */
const NF_BG_PC = "/images/404 page/404-bg_pc.webp";
const NF_BG_MOBILE = "/images/404 page/404-bg_mobile.webp";

const NotFound = () => {
  const location = useLocation();
  const { language, t } = useLanguage();

  const seoLang = language as SEOLanguage;
  const seoData = useMemo(() => getPageSEO("notFound", seoLang), [seoLang]);
  const canonicalPath = getPathWithoutLanguage(location.pathname);

  useEffect(() => {
    console.error(
      "404 Error: User attempted to access non-existent route:",
      location.pathname,
    );
  }, [location.pathname]);

  return (
    <div className="flex flex-col bg-neutral-900" data-oid="05r1vit">
      <SEO
        title={seoData.title}
        description={seoData.description}
        keywords={seoData.keywords}
        canonical={canonicalPath}
        language={seoLang}
        noindex
        includeHrefLang={false}
      />
      <NewHeader data-oid="su8ss9q" />

      <div className="relative pb-20 lg:pb-0" data-oid="nf-hero-wrap">
        <OptimizedImage
          src={NF_BG_PC}
          alt=""
          className="pointer-events-none absolute inset-0 z-0 h-full w-full"
          priority
          fetchPriority="high"
          sizes="100vw"
          webpSrc={`${NF_BG_MOBILE} 1024w, ${NF_BG_PC} 1920w`}
          srcSet={`${NF_BG_MOBILE} 1024w, ${NF_BG_PC} 1920w`}
          blurDataURL={getNotFoundBlurPlaceholder(NF_BG_PC)}
          hideLoadingSpinner
          data-oid="nf-bg"
        />
        <div
          className="absolute inset-0 z-[1] bg-black/30"
          aria-hidden
          data-oid="nf-scrim"
        />

        <main
          className="relative z-10 flex min-h-[100dvh] flex-col items-center justify-center px-4 py-28 lg:py-36"
          data-oid="qc000pd"
        >
          <div
            className="w-full max-w-lg rounded-2xl border border-white/40 bg-gradient-to-b from-white/[0.42] via-white/[0.28] to-white/[0.18] px-8 py-10 text-center shadow-[0_16px_48px_rgba(0,0,0,0.18),inset_0_1px_0_rgba(255,255,255,0.65),inset_0_0_0_1px_rgba(255,255,255,0.12)] backdrop-blur-2xl backdrop-saturate-[1.35]"
            data-oid="nf-card"
          >
            <h1
              className="font-playfair text-2xl font-semibold uppercase tracking-[0.12em] text-neutral-900 sm:text-3xl"
              data-oid="ws7ij3:"
            >
              {t("notFound.title")}
            </h1>
            <p
              className="mt-6 font-lato text-sm leading-relaxed text-neutral-700 sm:text-base"
              data-oid="tbl4nib"
            >
              {t("notFound.description")}
            </p>

            <div
              className="mt-10 flex flex-col gap-3 sm:flex-row sm:justify-center sm:gap-4"
              data-oid="nf-ctas"
            >
              <a
                href={getLocalizedUrl("/", language)}
                className="inline-flex min-h-[44px] items-center justify-center rounded-full bg-neutral-900 px-8 font-lato text-sm font-medium uppercase tracking-wide text-white transition-colors hover:bg-neutral-800"
                data-oid="7wr6v7k"
              >
                {t("notFound.ctaHome")}
              </a>
              <a
                href={getLocalizedUrl("/spa", language)}
                className="inline-flex min-h-[44px] items-center justify-center rounded-full border border-neutral-400/50 bg-white/50 px-8 font-lato text-sm font-medium uppercase tracking-wide text-neutral-900 shadow-sm backdrop-blur-sm transition-colors hover:bg-white/70"
                data-oid="nf-cta-spa"
              >
                {t("notFound.ctaSpa")}
              </a>
            </div>
          </div>
        </main>
      </div>

      <Footer data-oid="nf-footer" />
    </div>
  );
};

export default NotFound;
